import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	Signal,
} from '@angular/core';
import { TitleInsuranceProvider } from '@models/title-insurance';
import { select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessDispatch, BusinessState } from '@state/business';
import { ContentHeaderTitleImg } from 'src/app/ui/content-header/content-header.component';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { BusinessSettingsCardProvidersFirestorePaths } from '../business-finances-general/business-finances-general.types';
import { BaseBusinessSettingsSection } from '../business-finances-general/common/base-business-settings-section/base-business-settings-section.directive';
import { TestHooks } from './title-insurance-general.hooks';
import { SegmentedButtonOption } from 'src/app/ui/button-segmented/button-segmented.component';

const TitleInsProviderOptions = [
	{
		value: TitleInsuranceProvider.FCT,
		imagePath: '/assets/images/integrations/fct-logo.svg',
		label: 'First Canadian Title',
		classes: 'max-h-10',
	},
	{
		value: TitleInsuranceProvider.CHICAGO,
		imagePath: '/assets/images/integrations/chicago-title-logo.svg',
		label: 'Chicago Title',
		classes: 'max-h-10',
	},
	{
		value: TitleInsuranceProvider.STEWART,
		imagePath: '/assets/images/integrations/stewart-title-logo.svg',
		label: 'Stewart Title',
		classes: 'max-h-8',
	},
	{
		value: TitleInsuranceProvider.TITLE_PLUS,
		imagePath: '/assets/images/integrations/title-plus-logo.svg',
		label: 'TitlePLUS',
		classes: 'max-h-8',
	},
	{
		value: TitleInsuranceProvider.MANUAL,
		label: 'Manual',
	},
];

const extendCounselCreditOptions: SegmentedButtonOption[] = [
	{
		label: 'Yes',
		id: 1,
	},
	{
		label: 'No',
		id: 0,
	},
];

@Component({
	selector: 'app-title-insurance-general',
	templateUrl: './title-insurance-general.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TitleInsuranceGeneralComponent extends BaseBusinessSettingsSection {
	testHooks = TestHooks;
	private analytics = inject(AnalyticsService);
	private businessDispatch = inject(BusinessDispatch);

	public directionOfAmount_ = select(BusinessState.getDirectionOfAmount);
	public defaultSettings_ = select(BusinessState.getDefaultSettings);
	public defaultProvider_ = select(
		BusinessState.getDefaultTitleInsuranceProvider,
	);

	public titleImgConfig_: Signal<ContentHeaderTitleImg> = computed(() => {
		const currTitleInsProvider = this.defaultProvider_();

		const matchedProvider = this.titleInsProviderOptions.find(
			(option) => option.value === currTitleInsProvider,
		);

		if (!matchedProvider || !matchedProvider.imagePath) return null;

		return {
			imgSrc: matchedProvider.imagePath,
			altText: matchedProvider.label,
			classes: matchedProvider.classes || '',
		};
	});

	public doaLocations = DirectionOfFeeType;
	public titleInsuranceProvider = TitleInsuranceProvider;
	public titleInsProviderOptions = TitleInsProviderOptions;
	extendCounselCreditOptions = extendCounselCreditOptions;

	setDefaultProvider(provider: TitleInsuranceProvider): void {
		const currTitleInsProvider = this.defaultProvider_();

		if (provider === currTitleInsProvider) return;

		const titleInsField =
			BusinessSettingsCardProvidersFirestorePaths.TitleInsurance;

		this.businessDispatch.OverwriteBusinessFields(
			`defaultSettings.${titleInsField}.defaultProvider`,
			provider,
		);

		if (provider === TitleInsuranceProvider.TITLE_PLUS) {
			this.analytics.logEvent(AnalyticsEvent.TITLE_PLUS_DEFAULT_PROVIDER_CLICK);
		}
	}

	setSubjectToHST(subjectToHST: boolean): void {
		this.businessDispatch.OverwriteBusinessFields(
			'defaultSettings.titleInsurance.directionOfAmount.subjectToHSTInSOA',
			subjectToHST,
		);
	}

	async setExtendConnectingCounsel(
		extendConnectingCounsel: boolean,
	): Promise<void> {
		this.businessDispatch.OverwriteBusinessFields(
			'defaultSettings.titleInsurance.extendConnectingCounsel',
			Boolean(extendConnectingCounsel),
		);

		const settings = this.defaultSettings_();

		if (
			Boolean(extendConnectingCounsel) &&
			!!settings?.titleInsurance?.combineCounselFee
		) {
			this.setCombineCounselFee(false);
		}

		if (
			Boolean(extendConnectingCounsel) &&
			settings?.titleInsurance?.showCounselCredit
		) {
			this.setShowCounselCredit(false);
		}
	}

	setCombineCounselFee(combineCounselFee: boolean): void {
		this.businessDispatch.OverwriteBusinessFields(
			'defaultSettings.titleInsurance.combineCounselFee',
			combineCounselFee,
		);

		const settings = this.defaultSettings_();

		if (combineCounselFee && settings?.titleInsurance?.showCounselCredit) {
			this.setShowCounselCredit(false);
		}
	}

	extendConnectingCounsel_ = computed(() => {
		return this.defaultSettings_()?.titleInsurance?.extendConnectingCounsel
			? 1
			: 0;
	});

	setShowCounselCredit(showCounselCredit: boolean): void {
		this.businessDispatch.OverwriteBusinessFields(
			'defaultSettings.titleInsurance.showCounselCredit',
			showCounselCredit,
		);
	}

	hasShowCounselCredit_ = computed(() => {
		return (
			!this.defaultSettings_()?.titleInsurance?.extendConnectingCounsel &&
			!this.defaultSettings_()?.titleInsurance?.combineCounselFee
		);
	});
}
